import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

/**
 * Seed tokens for ConfigProvider. Components should read runtime values via
 * `theme.useToken()` — do not import these hexes into JSX.
 *
 * Primary / link / info stay Ant Design default blue (#1677ff). We only
 * override layout surface: warm paper, light sider, 8px radius.
 *
 * Ant Design 6 enables CSS variables by default. We rely on that and do
 * not set `cssVar: false`.
 */
const paper = '#F4F3F1';
const surface = '#FFFFFF';

/** Kept for existing page spacing. Prefer `token.margin*` in new code. */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const layout = {
  siderWidth: 220,
  siderCollapsedWidth: 72,
  headerHeight: 56,
} as const;

const sharedComponents: ThemeConfig['components'] = {
  Layout: {
    headerBg: surface,
    headerHeight: layout.headerHeight,
    headerPadding: `0 ${spacing.lg}px`,
    bodyBg: paper,
    siderBg: surface,
    lightSiderBg: surface,
    footerBg: paper,
  },
  Menu: {
    itemBg: 'transparent',
    subMenuItemBg: 'transparent',
    itemBorderRadius: 8,
    itemMarginInline: 8,
    itemMarginBlock: 2,
  },
  Button: {
    primaryShadow: 'none',
  },
};

export const getLightTheme = (): ThemeConfig => ({
  algorithm: theme.defaultAlgorithm,
  token: {
    colorBgLayout: paper,
    colorBgContainer: surface,
    borderRadius: 8,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  },
  components: sharedComponents,
});

export const getDarkTheme = (): ThemeConfig => ({
  algorithm: theme.darkAlgorithm,
  token: {
    colorBgLayout: '#0C0A09',
    colorBgContainer: '#18181B',
    borderRadius: 8,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  },
  components: {
    ...sharedComponents,
    Layout: {
      ...sharedComponents.Layout,
      headerBg: '#18181B',
      siderBg: '#18181B',
      lightSiderBg: '#18181B',
      bodyBg: '#0C0A09',
      footerBg: '#0C0A09',
    },
    Button: {
      primaryShadow: 'none',
    },
  },
});

export const getCompactTheme = (isDark: boolean): ThemeConfig => {
  const base = isDark ? getDarkTheme() : getLightTheme();
  return {
    algorithm: isDark
      ? [theme.darkAlgorithm, theme.compactAlgorithm]
      : [theme.defaultAlgorithm, theme.compactAlgorithm],
    token: {
      ...base.token,
      controlHeight: 28,
      fontSize: 13,
    },
    components: base.components,
  };
};
